/**
 * @file API 请求封装模块
 * @description 基于 @umijs/max 的 request 进行二次封装，提供统一的请求方法
 * @module services/api/request
 *
 * @example
 * // POST 请求示例
 * const result = await post<UserInfo>('/api/user/info', { id: 1 });
 *
 * // GET 请求示例
 * const data = await get<ListData>('/api/list', { page: 1 });
 *
 * // 文件上传示例
 * const formData = new FormData();
 * formData.append('file', file);
 * const res = await upload<string>('/api/upload', formData);
 *
 * // 文件下载示例
 * const blob = await download('/api/export', { type: 'excel' });
 */
import { request } from '@umijs/max';

/**
 * POST 请求封装
 * @template T - 响应数据类型
 * @param {string} url - 请求地址
 * @param {any} [params] - 请求参数（会放入 request body）
 * @param {any} [options] - 额外的请求配置（会覆盖默认配置）
 * @returns {Promise<RequestResult<T>>} 请求结果
 *
 * @example
 * const result = await post<UserInfo>('/user/login', { username: 'admin', password: '123456' });
 */
export const post = <T>(url: string, params?: any, options?: any) =>
  request(url, {
    method: 'POST',
    data: params,
    ...options,
  }) as Promise<RequestResult<T>>;

/**
 * GET 请求封装
 * @template T - 响应数据类型
 * @param {string} url - 请求地址
 * @param {any} params - 请求参数（会放入 query string）
 * @param {any} [options] - 额外的请求配置
 * @returns {Promise<RequestResult<T | string>>} 请求结果
 *
 * @example
 * const result = await get<ListData>('/api/list', { page: 1, size: 10 });
 */
export const get = <T>(url: string, params: any, options?: any) =>
  request(url, {
    method: 'GET',
    data: params,
    ...options,
  }) as Promise<RequestResult<T>>;

  // request<RequestResult<T | string>>(url, {
  //   method: 'GET',
  //   data: params,
  //   ...options,
  // });

/**
 * 文件上传封装
 * @template T - 响应数据类型
 * @param {string} url - 上传地址
 * @param {any} params - 上传参数（通常为 FormData，包含文件）
 * @param {any} [options] - 额外的请求配置
 * @returns {Promise<RequestResult<T | string>>} 上传结果
 *
 * @example
 * const formData = new FormData();
 * formData.append('file', file);
 * formData.append('type', 'avatar');
 * const result = await upload<string>('/api/upload', formData);
 */
export const upload = <T>(url: string, params: any, options?: any) =>
  request(url, {
    method: 'POST',
    data: params,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...options,
  }) as Promise<RequestResult<T>>;

/**
 * 文件下载封装
 * @template T - 响应数据类型
 * @param {string} url - 下载地址
 * @param {any} params - 请求参数
 * @param {any} [options] - 额外的请求配置
 * @returns {Promise<RequestResult<T | string>>} 下载结果（Blob 格式）
 *
 * @example
 * const blob = await download('/api/export', { type: 'excel', ids: [1, 2, 3] });
 * // 使用 aTagDownload 工具函数下载
 * aTagDownload(blob, 'export.xlsx');
 */
export const download = <T>(url: string, params: any, options?: any) =>
  request<RequestResult<T | string>>(url, {
    method: 'POST',
    data: params,
    responseType: 'blob',
    ...options,
  });
